/**
 * useGlobalSearchIndex — Construit l'index de recherche global côté client.
 *
 * Indexe :
 *   • Articles Magazine (18)     — /magazine/:slug
 *   • Enquêtes publiées (31)     — /enquete/:slug
 *   • Livrets et ressources      — /livret-plateau, /ressources-pedagogiques, etc.
 *   • Pages clés statiques       — Home, Formation, Tutos, Charte, Contact
 *
 * Retourne un tableau plat d'entrées `{ id, kind, title, subtitle, keywords, url, category }`
 * consommable par `GlobalSearchDialog`.
 *
 * Pas d'appel réseau : tout est bundlé au moment du build depuis les
 * fichiers `data/magazine/*.js` et `data/magazinesEnquetes/*.js`.
 */
import { useMemo } from 'react';
import { MAGAZINE_ARTICLES } from '../data/magazine';
import { ENQUETES_INDEX } from '../data/magazinesEnquetes';

const stripTags = (html) =>
  (html || '').replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();

export function useGlobalSearchIndex() {
  return useMemo(() => {
    const entries = [];

    // ── 1. Articles Magazine ───────────────────────────────────────────
    MAGAZINE_ARTICLES.forEach((a) => {
      entries.push({
        id: `mag-${a.slug}`,
        kind: 'magazine',
        category: 'Dossiers magazine',
        title: a.title || a.shortTitle,
        subtitle: stripTags(a.subtitle) || a.kicker || '',
        keywords: `${a.slug} ${a.seoKeywords || ''} ${a.category || ''} ${a.shortTitle || ''}`,
        url: `/magazine/${a.slug}`,
        badge: a.category || 'Magazine',
      });
    });

    // ── 2. Enquêtes publiées ───────────────────────────────────────────
    ENQUETES_INDEX.filter((e) => e.status === 'published').forEach((e) => {
      entries.push({
        id: `enq-${e.slug}`,
        kind: 'enquete',
        category: 'Enquêtes magazine',
        title: e.title || e.shortTitle,
        subtitle: stripTags(e.subtitle) || e.kicker || '',
        keywords: `${e.slug} ${e.seoKeywords || ''} ${e.tag || ''} ${e.shortTitle || ''}`,
        url: `/enquete/${e.slug}`,
        badge: e.tag || 'Enquête',
      });
    });

    // ── 3. Livrets & ressources pédagogiques ───────────────────────────
    const ressources = [
      {
        id: 'res-livret-plateau',
        title: 'Livret interactif Plateau A1/A2',
        subtitle: 'Ton parcours en 7 chapitres — 6 manœuvres officielles, cases à cocher, PDF téléchargeable',
        keywords: 'livret plateau A1 A2 manoeuvres officielles progression carnet',
        url: '/livret-plateau',
        badge: 'Livret',
      },
      {
        id: 'res-livret-circulation',
        title: 'Livret interactif Circulation A1/A2',
        subtitle: 'Ton parcours circulation en 7 chapitres — 40 min sur route, trajectoire de sécurité, autres usagers',
        keywords: 'livret circulation A1 A2 route trajectoire autres usagers question orale examen',
        url: '/livret-circulation',
        badge: 'Livret',
      },
      {
        id: 'res-cours-premiere-circulation-a2',
        title: 'Cours premium — Ma première circulation A2',
        subtitle: '14 chapitres pédagogiques illustrés : préparation, groupe, radio, incidents, méthode d\'intersection, bilan et 7 réflexes',
        keywords: 'cours premium premiere circulation A2 leçon enseignant élève groupe radio intersection giratoire bilan réflexes présentation slide 14 chapitres',
        url: '/cours/premiere-circulation-a2',
        badge: 'Cours',
      },
      {
        id: 'res-cours-deuxieme-circulation-a2',
        title: 'Cours premium — Ma 2ᵉ circulation A2',
        subtitle: 'Observer • Anticiper • Adapter — 14 chapitres : regard, indices, espace de sécurité, intersections, giratoires en autonomie, lecture des véhicules',
        keywords: 'cours premium deuxieme 2e circulation A2 observer anticiper adapter regard indices espace intersection giratoire autonomie leader bilan 14 chapitres',
        url: '/cours/deuxieme-circulation-a2',
        badge: 'Cours',
      },
      {
        id: 'res-cours-troisieme-circulation-a2',
        title: 'Cours premium — Ma 3ᵉ circulation A2',
        subtitle: 'Décider • Planifier • S\'adapter • Devenir autonome — 14 chapitres : regard loin, hiérarchisation, changements de voie, dépassement, virages, décisions autonomes',
        keywords: 'cours premium troisieme 3e circulation A2 decider planifier adapter autonome regard loin hierarchisation changement voie depassement virages 14 chapitres',
        url: '/cours/troisieme-circulation-a2',
        badge: 'Cours',
      },
      {
        id: 'res-cours-quatrieme-circulation-a2',
        title: 'Cours premium — Ma 4ᵉ circulation A2',
        subtitle: 'Conduire • Anticiper • Choisir • Sécuriser • Être autonome — 14 chapitres : autonomie, suivi de direction, imprévu, renoncement, partage, courtoisie, régularité, auto-analyse',
        keywords: 'cours premium quatrieme 4e circulation A2 conduire anticiper choisir securiser autonome autonomie direction imprevu renoncer partage courtoisie regularite auto-analyse examen 14 chapitres',
        url: '/cours/quatrieme-circulation-a2',
        badge: 'Cours',
      },
      {
        id: 'res-cours-cinquieme-circulation-a2',
        title: 'Cours premium — Ma 5ᵉ circulation A2',
        subtitle: 'Consolider • S\'adapter • Rester régulier • Se rapprocher du niveau examen — 14 chapitres : autonomie immédiate, radio discrète, itinéraire, marges, situations complexes, voie rapide, récupération, séquence type examen',
        keywords: 'cours premium cinquieme 5e circulation A2 consolider adapter regulier niveau examen autonomie radio discrete itineraire marges situations complexes voie rapide erreur recuperation debriefing 14 chapitres',
        url: '/cours/cinquieme-circulation-a2',
        badge: 'Cours',
      },
      {
        id: 'res-cours-sixieme-circulation-a2',
        title: 'Cours premium — Ma 6ᵉ circulation A2',
        subtitle: 'Valider • Consolider • Être autonome • Se préparer à l\'examen — 14 chapitres : mobilisation complète, environnements variés, lecture avancée, marges, décisions, imprévu, autonomie, régularité, séquence conditions d\'évaluation, bilan final',
        keywords: 'cours premium sixieme 6e circulation A2 valider consolider autonome preparer examen mobilisation complete environnements varies lecture avancee marges decisions imprevu autonomie regularite conditions evaluation bilan final 12 heures 14 chapitres',
        url: '/cours/sixieme-circulation-a2',
        badge: 'Cours',
      },
      {
        id: 'res-cours-premiere-plateau-a2',
        title: 'Cours premium — Ma 1ʳᵉ séance plateau A2',
        subtitle: 'Tu découvres • Tu ressens • Tu contrôles ta moto — 14 chapitres',
        keywords: 'cours premium premiere 1ere plateau A2 hors circulation moto commandes installation point patinage embrayage frein arriere regard allure lente demi tour rapports freinage 14 chapitres',
        url: '/cours/premiere-plateau-a2',
        badge: 'Cours',
      },
      {
        id: 'res-cours-deuxieme-plateau-a2',
        title: 'Cours premium — Ma 2ᵉ séance plateau A2',
        subtitle: 'Tu stabilises • Tu coordonnes • Tu diriges • Tu gagnes en précision — 14 chapitres',
        keywords: 'cours premium deuxieme 2e plateau A2 stabiliser coordonner diriger precision allure lente stable coordination commandes regard anticipation demi tour portes enchainements arrets transitions allure freinage 14 chapitres',
        url: '/cours/deuxieme-plateau-a2',
        badge: 'Cours',
      },
      {
        id: 'res-cours-troisieme-plateau-a2',
        title: 'Cours premium — Ma 3ᵉ séance plateau A2',
        subtitle: 'Tu accélères • Tu freines • Tu évites • Tu enchaînes — 14 chapitres',
        keywords: 'cours premium troisieme 3e plateau A2 accelerer freiner eviter enchainer transitions rapports freinage progressif evitement contre braquage direction allure elevee zone libre enchainement 14 chapitres',
        url: '/cours/troisieme-plateau-a2',
        badge: 'Cours',
      },
      {
        id: 'res-cours-quatrieme-plateau-a2',
        title: 'Cours premium — Ma 4ᵉ séance plateau A2 (FINAL)',
        subtitle: 'Tu consolides • Tu enchaînes • Tu t\'auto-corriges • Tu deviens autonome — 14 chapitres · Bilan final plateau 8 h minimales',
        keywords: 'cours premium quatrieme 4e plateau A2 final consolider enchainer auto correction autonome regularite reproductibilite passager conditions examen bilan plateau final 8 heures minimales 14 chapitres',
        url: '/cours/quatrieme-plateau-a2',
        badge: 'Cours',
      },
      {
        id: 'res-hub',
        title: 'Ressources pédagogiques',
        subtitle: 'Hub central : livrets, fiches imprimables, PDF, guides plateau et circulation',
        keywords: 'ressources pédagogiques hub livrets fiches PDF plateau circulation',
        url: '/ressources-pedagogiques',
        badge: 'Ressources',
      },
      {
        id: 'res-tutos',
        title: 'Tutos vidéo',
        subtitle: 'Playlists thématiques : Sécurité Routière officielle, moto plateau, circulation',
        keywords: 'tutos vidéo playlist sécurité routière youtube demonstration',
        url: '/tutos-video',
        badge: 'Tutos',
      },
      {
        id: 'res-formation',
        title: 'Formation & permis',
        subtitle: 'AM/BSR, A1, A2, passerelle A2→A, formation 125 (7h) — parcours et démarches',
        keywords: 'formation permis AM BSR A1 A2 passerelle 125 7h moto scooter',
        url: '/formation',
        badge: 'Formation',
      },
    ];
    ressources.forEach((r) =>
      entries.push({ ...r, kind: 'ressource', category: 'Livrets & ressources' })
    );

    // ── 4. Pages clés statiques ────────────────────────────────────────
    const pages = [
      {
        id: 'page-home',
        title: 'Accueil',
        subtitle: 'Le magazine pédagogique moto — hero éditorial hebdomadaire',
        keywords: 'accueil home page principale magazine hero édition',
        url: '/',
        badge: 'Accueil',
      },
      {
        id: 'page-magazine-hub',
        title: 'Magazine — Hub complet',
        subtitle: 'Tous les dossiers pédagogiques par thème (route, circulation, EPI, mécanique...)',
        keywords: 'magazine hub dossiers thématiques index sommaire',
        url: '/magazine',
        badge: 'Hub',
      },
      {
        id: 'page-enquetes',
        title: 'Enquêtes — Toutes les enquêtes',
        subtitle: 'Nos 31 enquêtes magazine sur la sécurité routière moto',
        keywords: 'enquêtes magazine investigation témoignages statistiques accidents',
        url: '/enquetes',
        badge: 'Hub',
      },
      {
        id: 'page-panneaux',
        title: 'Panneaux — Tous les panneaux de signalisation',
        subtitle: 'Bibliothèque complète des panneaux moto (danger, obligation, indication...)',
        keywords: 'panneaux signalisation danger obligation stop cédez le passage',
        url: '/panneaux',
        badge: 'Guide',
      },
      {
        id: 'page-sanctions',
        title: 'Barème des sanctions',
        subtitle: 'Points, amendes, suspensions — toutes les sanctions moto expliquées',
        keywords: 'sanctions permis points amendes retrait suspension permis blanc',
        url: '/sanctions-baremes',
        badge: 'Guide',
      },
      {
        id: 'page-assr',
        title: 'ASSR — Attestation Scolaire de Sécurité Routière',
        subtitle: 'ASSR 1 (5ᵉ) et ASSR 2 (3ᵉ) : programme, quiz, préparation',
        keywords: 'ASSR 1 2 attestation scolaire collège 5e 3e sécurité routière brevet BSR',
        url: '/assr',
        badge: 'ASSR',
      },
      {
        id: 'page-ressources-off',
        title: 'Vidéos officielles Sécurité Routière',
        subtitle: 'Campagnes et clips officiels du ministère de l\'Intérieur',
        keywords: 'vidéos officielles sécurité routière ministère intérieur campagne',
        url: '/ressources-officielles',
        badge: 'Officiel',
      },
      {
        id: 'page-charte',
        title: 'Notre charte éditoriale',
        subtitle: 'Positionnement magazine indépendant, sources officielles, engagements',
        keywords: 'charte éditoriale positionnement indépendant sources ONISR REMC transparence',
        url: '/charte-editoriale',
        badge: 'Éditorial',
      },
      {
        id: 'page-contact',
        title: 'Nous contacter',
        subtitle: 'Écris-nous : question, correction, demande éditoriale',
        keywords: 'contact email écrire question support aide',
        url: '/contact',
        badge: 'Contact',
      },
      {
        id: 'page-mentions',
        title: 'Mentions légales',
        subtitle: 'Éditeur, hébergeur, CGU, RGPD, cookies',
        keywords: 'mentions légales éditeur hébergeur CGU RGPD cookies vie privée',
        url: '/mentions-legales',
        badge: 'Légal',
      },
    ];
    pages.forEach((p) =>
      entries.push({ ...p, kind: 'page', category: 'Pages du site' })
    );

    return entries;
  }, []);
}

/**
 * Filtre une liste d'entrées d'après une requête utilisateur.
 * Recherche insensible à la casse et aux accents, sur title + subtitle + keywords.
 */
export function filterSearchEntries(entries, query) {
  if (!query || query.trim().length < 2) return [];
  const normalize = (s) =>
    (s || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  const q = normalize(query.trim());
  const words = q.split(/\s+/).filter(Boolean);

  return entries
    .map((e) => {
      const haystack = normalize(`${e.title} ${e.subtitle} ${e.keywords}`);
      // Score = nombre de mots matchés
      const score = words.reduce((acc, w) => acc + (haystack.includes(w) ? 1 : 0), 0);
      const allMatch = score === words.length;
      // Bonus si match dans le titre
      const titleMatch = words.some((w) => normalize(e.title).includes(w));
      return { entry: e, score, allMatch, titleMatch };
    })
    .filter((r) => r.allMatch)
    .sort((a, b) => {
      if (a.titleMatch && !b.titleMatch) return -1;
      if (!a.titleMatch && b.titleMatch) return 1;
      return b.score - a.score;
    })
    .map((r) => r.entry);
}
